import type { CSSProperties } from "react";
import { cssClass } from "./constant";

export type SpellingStylesType = {
  outerDiv: CSSProperties;
  wordRow: CSSProperties;
  letterBox: CSSProperties;
  letterBoxRevealed: CSSProperties;
  letterBoxCorrect: CSSProperties;
  letterBoxWrong: CSSProperties;
  letterFontSize: number;
  /** Box edge length in render pixels — mobile needs the raw number for the same reason web does. */
  boxSize: number;
  /** Inter-box (and inter-row) gap in render pixels, for the same reason as `boxSize`. */
  gap: number;
  /** How many boxes fit per row at this box size — see `SpellingBoxDimensions`. */
  boxesPerRow: number;
  /** How many rows the word wraps into at this box size. */
  rowCount: number;
  /** True when the element's CURRENT saved height already accommodates `rowCount` rows without
   * clipping. False means the frame needs to grow — the editor is responsible for persisting a
   * taller `element.size.height`, this package only reports whether it's currently enough. */
  fitsInFrame: boolean;
};

export interface SpellingBoxDimensions {
  boxSize: number;
  gap: number;
  fontSize: number;
  boxesPerRow: number;
  rowCount: number;
  /** The full content height (all rows + inter-row gaps) this layout needs, in the same render-px
   * units as `elementHeight`/`elementWidth` — NOT yet adjusted for outer padding. See
   * `getRequiredSpellingElementHeight` for the frame height that actually contains it. */
  contentHeight: number;
  /** See `SpellingStylesType.fitsInFrame`. */
  fitsInFrame: boolean;
}

// Matches cssClass.outerDiv's 3% padding on each side (6% of each dimension lost to padding).
const OUTER_PADDING_RATIO = 0.06;
// Gap between boxes, proportional to box size so spacing scales with the boxes themselves.
const GAP_RATIO = 0.15;
// Letter glyph size, relative to its box.
const FONT_RATIO = 0.45;
// Floor so a very long word or a very small frame never collapses to unreadable/zero-size boxes.
// When this floor wins, the row keeps the readable box size and wraps into more rows instead of
// shrinking further. At 20 the letter glyph was only 9px (FONT_RATIO below), legible on a 480px-
// wide editor canvas but not on a phone — raised to keep every wrapped/scaled box readable.
const MIN_BOX_SIZE = 28;
// Slack subtracted from the width budget before solving for box size, so the row's true occupied
// width is never solved to exactly 100% of the available width. Web (CSS flexbox) and mobile
// (Yoga) are two independent layout engines; even where they're supposed to agree (see the
// box-sizing note below), floating-point layout math can still round a fraction of a pixel
// differently between them. With zero slack, "n boxes + (n-1) gaps == availableWidth" holds
// exactly, so that rounding difference is enough to flip whether the last box fits on web but not
// mobile (or vice versa) — a real bug this project hit. A few percent of slack is cheap and makes
// the row's fit robust to that class of engine difference instead of relying on exact agreement.
const WIDTH_SAFETY_MARGIN_RATIO = 0.05;

// Teacher-adjustable multiplier on the auto-fitted box size (SpellingElementSchema.boxScale in
// packages/types). Deliberately a ratio applied on top of the frame-derived size, never an
// absolute pixel value — every size in this element is frame-relative so web and mobile render
// identically at any resolution. Clamped so the boxes can neither collapse to unreadable nor be
// scaled to an absurd multiple of the frame.
export const MIN_BOX_SCALE = 0.5;
export const MAX_BOX_SCALE = 3;
export const DEFAULT_BOX_SCALE = 1;
/** Step the web toolbar's -/+ buttons move `boxScale` by. */
export const BOX_SCALE_STEP = 0.1;

/**
 * Clamps a teacher-authored box scale into the safe range, defaulting an absent or invalid value
 * to `DEFAULT_BOX_SCALE` (1, i.e. the unscaled auto-fit size every existing element renders at
 * today). This is the one place that logic lives, so both platforms stay in sync. Exported so the
 * web toolbar can clamp before dispatching, and so the clamp itself is independently testable.
 */
export function clampBoxScale(boxScale: number | undefined): number {
  if (boxScale === undefined || Number.isNaN(boxScale)) return DEFAULT_BOX_SCALE;
  return Math.min(Math.max(boxScale, MIN_BOX_SCALE), MAX_BOX_SCALE);
}

/**
 * Pure sizing math, extracted so it's testable without rendering: given the element's own frame
 * and how many letter boxes it must hold, returns the square box size to render at, plus how many
 * rows that wraps into.
 * Takes no platform argument and branches on nothing platform-specific — web and mobile both call
 * this exact function with the same numeric inputs, which is what keeps them in sync; there is
 * deliberately no separate "web version" / "mobile version" of this math.
 *
 * The auto-fit *baseline* (before `boxScale`) is unchanged from before this function supported
 * wrapping: the largest box that fits the whole word on one row (by width, accounting for
 * inter-box gaps and safety slack) while never taller than the frame itself (by height) —
 * whichever constraint is tighter wins, floored at `MIN_BOX_SIZE`. This keeps every default
 * (unscaled) word/frame combination rendering exactly as it did before.
 *
 * `boxScale` then multiplies that baseline — and, unlike before, is no longer capped at the
 * frame's current height. A box (or a wrapped word) that needs more vertical room than the frame
 * currently has is the editor's job to grow the frame for (see `getRequiredSpellingElementHeight`)
 * and persist — never this package's job to silently clip or shrink back down to fit. `fitsInFrame`
 * reports whether the frame passed in is already tall enough, so a caller who isn't in a position
 * to grow the frame (e.g. a read-only render of an older, not-yet-migrated slide) can decide its
 * own safe fallback instead of rendering rows that spill out of the box.
 */
export function calculateSpellingBoxSize(
  elementWidth: number,
  elementHeight: number,
  letterCount: number,
  boxScale?: number
): SpellingBoxDimensions {
  const safeLetterCount = Math.max(letterCount, 1);
  const availableWidth = elementWidth * (1 - OUTER_PADDING_RATIO) * (1 - WIDTH_SAFETY_MARGIN_RATIO);
  const availableHeight = elementHeight * (1 - OUTER_PADDING_RATIO);

  // n boxes + (n-1) gaps, where each gap is a fraction of the (unknown) box size, must fit
  // availableWidth: n*box + (n-1)*GAP_RATIO*box <= availableWidth
  const widthFitBoxSize = availableWidth / (safeLetterCount + (safeLetterCount - 1) * GAP_RATIO);
  const heightFitBoxSize = availableHeight;

  // MIN_BOX_SIZE floors the *baseline*, before the scale multiplies it — not the final result.
  // Flooring afterwards made the control dead for exactly the elements that need it: a 17-letter
  // phrase in the default frame auto-fits to ~15px, so the teacher would render at the 28px floor
  // at 100%, and still at 28px at 150%, because 15 * 1.5 is under the floor too. Nothing moved
  // until ~190%. Flooring first makes 100% mean "what you see now" for every word length, and
  // every step from there proportional.
  const autoFitBoxSize = Math.max(Math.min(widthFitBoxSize, heightFitBoxSize), MIN_BOX_SIZE);
  const boxSize = Math.max(autoFitBoxSize * clampBoxScale(boxScale), MIN_BOX_SIZE);
  const gap = boxSize * GAP_RATIO;

  // How many boxes (plus their gaps) actually fit across the frame's width at this box size —
  // this, not a single width-fits-all-letters check, is what decides wrapping now.
  const boxesPerRow = Math.max(1, Math.floor((availableWidth + gap) / (boxSize + gap)));
  const rowCount = Math.ceil(safeLetterCount / boxesPerRow);
  // Rows use the same GAP_RATIO spacing between them as boxes use between each other, so vertical
  // and horizontal spacing read as one consistent rhythm.
  const contentHeight = rowCount * boxSize + (rowCount - 1) * gap;

  return {
    boxSize,
    gap,
    fontSize: boxSize * FONT_RATIO,
    boxesPerRow,
    rowCount,
    contentHeight,
    fitsInFrame: contentHeight <= availableHeight,
  };
}

/**
 * The minimum `element.size.height` that would let this word/boxScale combination render at
 * `boxSize` without any row clipping — i.e. what the editor should grow the element to (never
 * shrinks below the frame's current height, since a teacher may have deliberately left extra
 * room). Inverse of the `availableHeight = elementHeight * (1 - OUTER_PADDING_RATIO)` step in
 * `calculateSpellingBoxSize`, so the two stay in lockstep by construction.
 */
export function getRequiredSpellingElementHeight(
  elementWidth: number,
  elementHeight: number,
  letterCount: number,
  boxScale?: number
): number {
  const { contentHeight } = calculateSpellingBoxSize(
    elementWidth,
    elementHeight,
    letterCount,
    boxScale
  );
  // +1px: a plain inverse of the padding step can round a hair short of `contentHeight` once
  // divided back out, which would make a fresh `calculateSpellingBoxSize` call at exactly this
  // height report `fitsInFrame: false` again — a whole extra pixel of headroom is cheap insurance
  // against that float round-trip, not a meaningful visual difference.
  const requiredHeight = contentHeight / (1 - OUTER_PADDING_RATIO) + 1;
  return Math.max(elementHeight, requiredHeight);
}

/**
 * Letter-box styling for the spelling question. Unlike every other question renderer — which all
 * derive their sizing purely from `canvasHeight` — spelling boxes are derived from the element's
 * own frame (`elementWidth`/`elementHeight`) and its letter count. This is a deliberate,
 * spelling-only divergence: box count is derived from the word rather than authored directly, so
 * the same frame must gracefully fit words of very different lengths, and the teacher resizes the
 * element itself (not the canvas) to control how big the letters render — or, when the frame is
 * the right size but the word is long enough to have auto-fitted itself down to unreadable boxes,
 * overrides that fit with `boxScale`. Web and mobile both call this one function so they stay
 * visually consistent, `boxScale` included.
 */
export function getSpellingStyles(
  elementWidth: number,
  elementHeight: number,
  letterCount: number,
  boxScale?: number
): SpellingStylesType {
  const { boxSize, gap, fontSize, boxesPerRow, rowCount, fitsInFrame } = calculateSpellingBoxSize(
    elementWidth,
    elementHeight,
    letterCount,
    boxScale
  );

  return {
    outerDiv: {
      ...cssClass.outerDiv,
      ...cssClass.fontFamily,
      justifyContent: "center",
      alignItems: "center",
      // Backward-compat safety net for a slide saved before wrapping existed (or edited elsewhere
      // without going through the editor's auto-grow effect in FillBlanksRenderer/TextRenderer/
      // SpellingRenderer): `cssClass.outerDiv` defaults to `overflow: hidden`, which would silently
      // clip whichever wrapped rows don't fit the old, too-short saved height. Switching to
      // `visible` only when the current frame is actually too short means every row stays visible
      // — at the cost of spilling past the frame's edge — until the editor is reopened and grows +
      // persists the correct height, at which point `fitsInFrame` flips true and this reverts to
      // the normal clip. Applies identically to Live View and mobile since both call this same
      // function; nothing renderer-specific needed.
      overflow: fitsInFrame ? "hidden" : "visible",
    },

    // Wraps into multiple centred rows instead of scrolling once the word no longer fits on one
    // row at a readable box size — `flexWrap: "wrap"` + `justifyContent: "center"` centres each
    // wrapped line independently under standard flexbox rules, which is exactly "additional
    // centred rows" with no extra logic needed. The element's frame is expected to be tall enough
    // to show every row without clipping — see `getRequiredSpellingElementHeight`, which the
    // editor uses to grow and persist the frame height whenever this wraps into more than one row.
    wordRow: {
      display: "flex",
      flexDirection: "row",
      gap,
      flexWrap: "wrap",
      justifyContent: "center",
      alignContent: "center",
    },

    // React Native's Yoga layout is always border-box: width/height already include border, so a
    // box declared `width: boxSize` with a 2px border still occupies exactly boxSize of row
    // width. Web only matches that if box-sizing is border-box too — this app's Tailwind Preflight
    // (`@tailwind base` in apps/b2b-web/src/styles/globals.css) sets `box-sizing: border-box`
    // globally, so it already does, but that's an external reset this shared package doesn't
    // control. Pinning it explicitly here (a no-op on mobile, since RN has no other box model)
    // means the two platforms can't silently diverge if that global reset ever changes or this
    // component renders somewhere without it.
    letterBox: {
      width: boxSize,
      height: boxSize,
      // Boxes never shrink to squeeze an extra one onto a row — wrapping to a new row is what
      // absorbs any overflow now, not the box size.
      flexShrink: 0,
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "0.5rem",
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: "#cbd5e1",
      backgroundColor: "#FFFFFF",
      textAlign: "center",
      // Pinned rather than inherited: the box paints its own white background, so a theme whose
      // default text colour is light (mobile's dark mode) would render the typed letter white on
      // white — invisible, with nothing on screen to explain why.
      color: "#1F2937",
    },

    letterBoxRevealed: {
      backgroundColor: "#F1F5F9",
      borderColor: "#94a3b8",
    },

    letterBoxCorrect: {
      backgroundColor: "rgb(35, 205, 0)",
      borderColor: "rgb(35, 205, 0)",
    },

    letterBoxWrong: {
      backgroundColor: "rgb(235, 45, 45)",
      borderColor: "rgb(235, 45, 45)",
    },

    letterFontSize: fontSize,
    boxSize,
    gap,
    boxesPerRow,
    rowCount,
    fitsInFrame,
  };
}
